import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ViewChildren,
  QueryList, 
  ViewContainerRef,
  ComponentFactoryResolver,
  ComponentRef,
  ChangeDetectorRef } from '@angular/core';
import * as Papa from 'papaparse';
import { Subject, Observable } from 'rxjs/Rx';
import 'rxjs/add/operator/switchMap';
import { UUID } from 'angular2-uuid';

import { DynamicRowsDirective } from './dynamic-rows.directive';
import { FormField, Link, Disclaimer } from './form-field.model';
import { FormSection } from './form-section.model';
import { FormRowComponent } from './form-row.component';
import { Template } from '../templates/template.model';
import { FieldComponent, UndoAction } from './field.component';
import { FadeInAnimation } from '../shared/fade-in.animation';
import { ListItemAnimation } from '../shared/list-item.animation';
import { HighlightAnimation } from '../shared/highlight.animation';
import { DynamicOverlaysDirective } from './dynamic-overlays.directive';
import { StickyHeaderComponent } from '../shared/sticky-header.component';

export interface Row {
  id: string,
  instance: FormRowComponent,
  ref: ComponentRef<FormRowComponent>,
  value: any,
  versionStatus?: string
}

export interface UndoAction {
  field: FieldComponent,
  value: any
}

@Component({
  selector: 'app-table-field',
  templateUrl: './table-field.component.html',
  styleUrls: ['./table-field.component.scss'],
  animations: [FadeInAnimation, ListItemAnimation(), HighlightAnimation]
})
export class TableFieldComponent extends FieldComponent implements OnInit {
  @Input() draftData: any;
  @Input() dynamicOverlays: DynamicOverlaysDirective;
  @Input() field: FormField;
  @Input() locked: boolean = false;
  @Input() lockValues: boolean = false;
  @Input() standard: any;
  @Input() templateData: any;
  @Input() workflowId: number;
  @Output() disclaimerChanged = new EventEmitter<{field: string, value: any}>();
  @Output() focusChanged = new EventEmitter<boolean>();
  @Output() highlightChanged = new EventEmitter<{highlighted: boolean, color?: string}>();
  @Output() listAdded = new EventEmitter<number>();
  @Output() rowLinkAdded = new EventEmitter<{field: string, id: string, value: any}>();
  @Output() rowLinkRemoved = new EventEmitter<{field: string, id: string}>();
  @Output() rowLinkUpdated = new EventEmitter<{field: string, id: string, value: any}>();
  @Output() resized = new EventEmitter<FieldComponent>();
  @Output() returnKey = new EventEmitter<FieldComponent>();
  @Output() stickyHeaderAdded = new EventEmitter<StickyHeaderComponent>();
  @Output() undoableAction = new EventEmitter<UndoAction>();
  @Output() updateLinked = new EventEmitter<{link: Link, value?: any, categories?: any[], enabled?: boolean}>();
  @Output() valueChanged = new EventEmitter<{field: string, value: any}>();
  @Output() viewLoaded = new EventEmitter<FieldComponent>();
  @ViewChild('dynamicRows', {read: DynamicRowsDirective}) dynamicRows: DynamicRowsDirective;
  @ViewChild(StickyHeaderComponent) stickyHeader: StickyHeaderComponent;
  @ViewChildren('disclaimers') disclaimerElements;

  rows: Row[] = [];
  sortedRows: Row[] = [];
  sorting: boolean = false;
  values = [];
  fields: FormField[];
  add: boolean = true;
  csv: boolean = false;
  csvData: string;
  defaultSortBy: string[] = [];
  draggable: boolean = false;
  dragging: boolean = false;
  defaultValues: any[];
  disclaimer: Disclaimer;
  disclaimerFields: {field: string, disclaimers: Disclaimer[]}[] = [];
  disclaimerState: string = "inactive";
  focused: boolean = false;
  highlighted: boolean = false;
  highlightColor: string;
  highlightLinkedId: string;
  id: string;
  label: string;
  links: Link[];
  loaded = false;
  looseLink: boolean;
  max: number;
  min: number;
  needsReorder: boolean = false;
  needsSort: boolean = false;
  previousValue: any;
  rowId: string;
  sortBy: string[] = [];
  sortId: string;
  sortDirection: string;
  sortTimer: any;
  splitRegex = /\b/;
  touched: boolean = false;
  valid: boolean = false;
  viewsLoaded: number = 0;
  versionChanged: boolean = false;
  width: number = 0;
  addingRow: boolean = false;
  get value() { return this.rows.filter((r) => !r.instance.blank && r.versionStatus != "removed").map((r) => r.value) }

  constructor(
    cd: ChangeDetectorRef,
    private resolver: ComponentFactoryResolver) { super(cd) }

  ngOnInit() {
    this.id = this.field.id;
    this.label = this.field.label;
    this.min = this.field.min;
    this.max = this.field.max;
    this.fields = this.field.fields;
    if (this.field.locked) { this.locked = true }
    this.csv = this.field.csv;
    this.links = this.field.links;
    this.highlightLinkedId = this.field.highlightLinkedId;
    if (this.field.add != null) { this.add = this.field.add };
    this.width = this.field.columns;
    if (this.field.sortBy != null) {
      if (this.field.sortBy instanceof Array) {
        this.defaultSortBy = this.field.sortBy;
      } else {
        this.defaultSortBy = [this.field.sortBy];
      }
    }
    this.sortBy = this.defaultSortBy;
    if (this.field.draggable) { this.draggable = true }

    this.loadDefaultValues();
    this.loadInitialState(this.draftData);
  }

  onViewLoaded(instance) {
    if (!this.loaded) {
      this.viewsLoaded += 1
      if (this.viewsLoaded >= this.rows.length) {
        this.loaded = true
        this.viewLoaded.emit(this);
        this.stickyHeaderAdded.emit(this.stickyHeader);
        setTimeout(() => {
          this.sortTable();
          this.updateDisclaimers();
        }, 100);
      }
    } else {
      this.addingRow = false;
      if (!instance.blank) {
        setTimeout(() => {
          this.updateRowOrder();
          this.autoIndex();
          this.sortTable();
          this.updateLinkedFields();
          this.resized.emit(this);
        }, 100)
      } else {
        setTimeout(() => this.resized.emit(this), 100);
      }
    }
  }

  validate() {
    this.valid = this.locked == true ||
      (this.rows.filter((r) => !r.instance.valid).length === 0 &&
      (this.min == null || this.rows.length >= this.min) &&
      (this.max == null || this.rows.length <= this.max))
  }

  loadDefaultValues() {
    if (this.templateData != null) {
      this.defaultValues = this.templateData
    } else {
      this.defaultValues = [];
    }
  }

  loadInitialState(draftValues?: any[]) {
    this._dirty = false;
    this.touched = false;

    let values = draftValues != null ? draftValues : this.defaultValues;
    for (let value of values) {
      this.addRow(value);
    }

    if (this.rows.length == 0) {
      this.addRow();
    }

    // Emit initial validity and value
    this.validate();
    this.emitValue();
  }

  reset() {
    for (let row of this.rows) {
      this.removeRow(row);
    }
    this.loadInitialState();
    this.cd.markForCheck();
  }

  checkDirty() {
    this._dirty = (this.rows.filter((r) => r.instance.dirty).length > 0)
  }

  resetDirty(templateData) {
    for (let row of this.rows) {
      let rowData = templateData.find((x) => x['_id'] == row.id);
      row.instance.resetDirty(rowData);
    }
    this.checkDirty();
  }

  checkTouch() {
    this.touched = (this.rows.filter((r) => r.instance.touched).length > 0)
  }

  disable() {
    super.disable();
    for (let row of this.rows) {row.instance.disable()}
  }

  enable() {
    super.enable();
    for (let row of this.rows) {row.instance.enable()};
  }

  onAddRow() {
    this.addRow(null, null, true);
  }

  addRow(templateData?, position?, userInitiated: boolean = false): Row {
    let container = this.dynamicRows.viewContainerRef;
    let rowId = templateData != null && templateData["_id"] != null ? templateData["_id"] : UUID.UUID();
    let rowValue: any = {"_id": rowId};

    let factory = this.resolver.resolveComponentFactory(FormRowComponent);
    let component = container.createComponent(factory, position);
    let instance = component.instance;
    let row = {
      id: rowId,
      instance: instance,
      ref: component,
      value: rowValue
    };

    instance.id = rowId;
    instance.fields = this.fields;
    instance.locked = this.locked;
    instance.lockValues = this.lockValues;
    instance.returnEnabled = false;
    instance.workflowId = this.workflowId;
    instance.standard = this.standard;
    if (userInitiated) {
      instance.focusOnLoad = true;
    }
    if (templateData != null) {
      instance.templateData = templateData;
      instance.blank = false;
    }

    instance.viewLoaded.subscribe(() => this.onViewLoaded(instance))
    instance.valueChanged.subscribe((event) => {
      this.checkDirty();
      this.checkTouch();
      rowValue[event.field] = event.value;
      if (!instance.blank) {
        if (this.sortBy.find((f) => f == event.field) != null) {
          this.needsSort = true;
        }
        if (this.defaultSortBy.find((f) => f == event.field) != null) {
          this.needsReorder = true;
        }
      }
      this.validate();
      this.emitValue();
    });
    instance.rowLinkAdded.subscribe((event) => this.rowLinkAdded.emit(event))
    instance.rowLinkRemoved.subscribe((event) => this.rowLinkRemoved.emit(event))
    instance.rowLinkUpdated.subscribe((event) => this.rowLinkUpdated.emit(event))
    instance.highlightChanged.subscribe((event) => {
      if (event.highlighted) {
        rowValue["_highlighted"] = true;
        rowValue["_highlight_color"] = event.color;
      } else {
        delete rowValue["_highlighted"];
        delete rowValue["_highlight_color"];
      }
      this.emitValue();
    })
    if (!this.locked && !(templateData != null && this.lockValues)) {
      instance.removable = true;
    }
    instance.removeRow.subscribe(() => {
      this.onRemoveRow(row);
    })
    if (!this.locked) { instance.copyable = true };
    instance.copyRow.subscribe(() => {
      this.onCopyRow(row);
    })
    instance.returnKey.subscribe(() => {
      this.onReturn(row);
    })
    instance.disclaimerChanged.subscribe((event) =>
      this.onDisclaimerChanged(event)
    )
    instance.showDisclaimer.subscribe((event) =>
      this.onShowDisclaimer(event)
    )
    instance.focusChanged.subscribe((event) => {
      this.focused = event;
      if (this.focused) {
        if (this.sortTimer != null) {
          clearTimeout(this.sortTimer);
        }
      } else {
        this.sortTimer = setTimeout(() => {
          if (!this.focused) {
            if (this.needsReorder) {
              this.updateRowOrder();
            } else {
              this.autoIndex();
            }
            if (this.needsSort) {
              this.sortTable();
            }
          }
        }, 500);
      }
    })
    instance.dynamicOverlays = this.dynamicOverlays;
    instance.listAdded.subscribe((event) => this.listAdded.emit(event));
    instance.undoableAction.subscribe((action) => this.undoableAction.emit(action));
    instance.blankChanged.subscribe((action) => this.autoIndex());
    instance.draggable = this.draggable;
    instance.dragDropped.subscribe((dragRowId) => this.onDragDrop(dragRowId, rowId))
    instance.dragEnded.subscribe(() => this.onDragEnd())
    instance.dragStarted.subscribe(() => this.onDragStart())

    if (position != null) {
      this.rows.splice(position, 0, row);
    } else {
      this.rows.push(row);
    }

    this.cd.markForCheck();
    return row;
  }

  onRemoveRow(row: Row) {
    let id = row.id;
    let blank = row.instance.blank;
    let data = row.value;
    let links = row.instance.links;
    let container = this.dynamicRows.viewContainerRef;
    let position = container.indexOf(row.ref.hostView);

    this.removeRow(row);
    this.touched = true;
    this._dirty = true;

    this.autoIndex();
    this.validate();
    this.emitValue();

    for (let link of links) {
      if (!link.addOnly) {
        this.rowLinkRemoved.emit({field: link.id, id: id})
      }
    }
    this.updateDisclaimers();
    if (!blank) {
      this.undoableAction.emit({
        description: "Row was removed",
        field: this,
        value: {data: data, position: position}
      });
    }

    if (this.rows.length == 0) {
      this.addRow();
    }
  }

  removeRow(row: Row) {
    let index = this.rows.indexOf(row);
    this.rows.splice(index, 1);
    row.ref.destroy();
    setTimeout(() => this.resized.emit(this), 100);
  }

  addLinkedRow(id, value) {
    let row = this.rows.find((r) => r.id === id);
    if (row == null) {
      if (this.rows.length == 1) {
        let first = this.rows[0]
        if (first.instance.blank) { this.removeRow(first) }
      }
      value["_id"] = id;
      row = this.addRow(value);
    }
  }

  updateLinkedRow(id, value) {
    let row = this.rows.find((r) => r.id === id);
    if (row == null) { return }
    for (let key in value) {
      row.value[key] = value[key];
    }
    row.instance.setValue(value, false);
    this.emitValue();
  }

  removeLinkedRow(id) {
    let row = this.rows.find((r) => r.id === id);
    if (row == null) { return }
    this.removeRow(row);
    this.autoIndex();
    this.validate();
    this.emitValue();
  }

  onCopyRow(row: Row) {
    this.copyRow(row);
    this.touched = true;
    this._dirty = true;

    this.updateRowOrder();
    this.validate();
    this.emitValue();
  }

  copyRow(row: Row) {
    if (this.addingRow) { return }
    this.addingRow = true;

    let value: any = {};
    for (let field of row.instance.components) {
      if (field.copyable) {
        value[field.id] = field.value;
      }
    }

    let container = this.dynamicRows.viewContainerRef;
    let position = container.indexOf(row.ref.hostView);
    this.addRow(value, position+1);
  }

  emitValue() {
    let value = JSON.parse(JSON.stringify(this.value));
    this.valueChanged.emit({field: this.id, value: value})
  }

  setValue(value, emit=false, looseLink=false) {
    for (let row of this.rows.slice()) {
      this.removeRow(row);
    }

    for (let rowValue of value) {
      this.addRow(rowValue);
    }

    if (this.rows.length == 0) {
      this.addRow();
    }

    this.updateRowOrder();
    this.autoIndex();
    this.sortTable();
    this.validate();
    if (emit) {
      this.emitValue();
    }
    this.cd.markForCheck();
  }

  setPreviousValue(value) {
    this.previousValue = value;
    let currentValue = this.value;
    let container = this.dynamicRows.viewContainerRef;
    for (let pv of this.previousValue) {
      let rowValue = currentValue.find((v) => v["_id"] === pv["_id"])
      if (rowValue == null) {
        // Row was removed
        let position = this.previousValue.indexOf(pv)
        //this.addVersionRow(pv, "removed", position);
      } else {
        let changed = false;
        for (let key in pv) {
          if (pv[key] !== rowValue[key]) {
            changed = true;
            break;
          }
        }
        if (changed) {
          // Row has changed
          let currentRow = this.rows.find((r) => r.id === pv["_id"]);
          currentRow.versionStatus = "changed";
          currentRow.instance.setPreviousValue(pv);
        }
      }
    }
    for (let cv of currentValue) {
      let pv = this.previousValue.find((v) => v["_id"] === cv["_id"]);
      if (pv == null) {
        // Row has been added
        let currentRow = this.rows.find((r) => r.id === cv["_id"]);
        currentRow.versionStatus = "new";
        currentRow.instance.setVersionStatus("new");
      }
    }
  }

  resetPreviousValue() {
    this.previousValue = null;
    for (let row of this.rows) {
      switch (row.versionStatus) {
      case "changed":
        row.instance.versionStatus = null;
        row.instance.resetPreviousValue();
        break;
      case "removed":
        this.removeRow(row);
        break;
      case "new":
        row.instance.versionStatus = null;
        row.instance.resetVersionStatus();
        break;
      }
      row.versionStatus = null;
    }
  }

  addVersionRow(value, status, position): Row {
    let container = this.dynamicRows.viewContainerRef;
    let rowValue = value;
    let rowId = value["_id"];

    let factory = this.resolver.resolveComponentFactory(FormRowComponent);
    let component = container.createComponent(factory, position);
    let instance = component.instance;
    let row = {
      id: rowId,
      instance: instance,
      ref: component,
      value: rowValue,
      versionStatus: status
    };

    instance.versionStatus = status;
    instance.id = rowId;
    instance.fields = this.fields;
    instance.templateData = value;
    instance.blank = false;
    instance.workflowId = this.workflowId;
    instance.standard = this.standard;
    instance.locked = true;
    instance.removable = false;
    instance.copyable = false;
    instance.dynamicOverlays = this.dynamicOverlays;
    instance.setVersionStatus(status);

    this.rows.push(row);

    return row;
  }

  positionOverlays() {
    for (let row of this.rows) {
      row.instance.positionOverlays();
    }
  }

  focus() {
    if (this.rows.length > 0) {
      this.rows[0].instance.focus();
    }
  }

  blur() {
    for (let row of this.rows) {
      row.instance.blur();
    }
  }

  onReturn(row: Row) {
    if (this.addingRow) { return }
    this.addingRow = true;

    let container = this.dynamicRows.viewContainerRef;
    let position = container.indexOf(row.ref.hostView);
    let newRow = this.addRow(null, position+1, true);
  }

  columnForField(field): string {
    if (field.columns != null) {
      let width = field.columns === 0.5 ? 'half' : field.columns;
      return 'column-'+width;
    } else {
      return 'column-rigid';
    }
  }

  compare(other): number { return 0 }

  onSortTable(fieldId: string) {
    if (this.sortId == fieldId) {
      // Already sorted by this column
      if (this.sortDirection == 'asc') {
        // Change sort direction
        this.sortDirection = 'desc';
      } else {
        // Reset sort to default
        this.sortId = this.defaultSortBy[0];
        this.sortBy = this.defaultSortBy;
        this.sortDirection = 'asc';
      }
    } else {
      // Change to new sort column
      this.sortId = fieldId;
      this.sortBy = [fieldId];
      this.sortDirection = 'asc';
    }
    this.sortTable();
  }

  sortTable() {
    this.needsSort = false;
    if (this.sorting) { return }
    this.sorting = true;

    let currentRows = this.sortedRows;
    let newRows = this.sortRows(this.rows, this.sortBy, this.sortDirection);
    
    let orderChanged = this.rowOrderChanged(currentRows, newRows)

    if (currentRows == null || orderChanged) {
      // This is an expensive operation, so we only do it if there actually was a change
      this.reorderRows(this.dynamicRows.viewContainerRef, newRows);
    }

    this.sortedRows = newRows;
    this.sorting = false;
  }

  rowOrderChanged(rowsA: Row[], rowsB: Row[]) {
    rowsA = rowsA.filter((x) => !x.instance.blank);
    rowsB = rowsB.filter((x) => !x.instance.blank);

    if (rowsA.length != rowsB.length) {
      return true;
    }

    for (let i = 0; i < rowsA.length; i++) {
      if (rowsA[i].id != rowsB[i].id) {
        return true;
      }
    }

    return false;
  }

  updateRowOrder() {
    this.needsReorder = false;
    if (this.defaultSortBy.length > 0) {
      this.rows = this.sortRows(this.rows, this.defaultSortBy, 'asc')
      this.autoIndex();
      this.emitValue();
    }
  }

  sortRows(rows: Row[], sortBy: string[], direction: string) {
    let sortedRows = rows.slice().sort((a,b) => {
      let result = 0;
      for (let fieldId of sortBy) {
        let af = a.instance.components.find((f) => f.id === fieldId);
        let bf = b.instance.components.find((f) => f.id === fieldId);
        if (af != null && bf != null) {
          result = af.compare(bf);
        }
        if (result != 0) { break }
      }
      return result
    })

    if (direction == "desc") {
      sortedRows = sortedRows.reverse();
    }

    return sortedRows;
  }

  reorderRows(container, rows) {
    let index = 0;
    for (let row of rows) {
      container.move(row.ref, index);
      index += 1;
    }
    this.positionOverlays();
  }

  generateCSV(event) {
    let fields = this.fields.map((f) => f.label);
    let data = this.value.map((value) => {
      let row = []
      for (let f of this.fields) {
        let column = value[f.id];
        if (column != null) {
          if (Array.isArray(column)) {
            column = column.map((c) => c.hasOwnProperty('stored') ? c.stored : c).join(', ')
          } else {
            column = column.hasOwnProperty('stored') ? column.stored : column;
          }
        }
        row.push(column);
      }
      return row;
    });
    let csvString = Papa.unparse({fields: fields, data: data}, {header: true});
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      // For internet explorer file save
      let fileName = this.field.id+'.csv';
      let blob = new Blob([csvString], {type: 'text/plain;charset=utf-8'})
      window.navigator.msSaveOrOpenBlob(blob, fileName);
      event.preventDefault()
    } else {
      this.csvData = 'data:text/plain;charset=utf-8,' + encodeURIComponent(csvString);
    }
  }

  setStandard(event: {standard: any, value: any}) {
    this.standard = event.standard;
    for (let row of this.rows) {
      row.instance.setStandard(event);
    }
  }

  convertStandard(event: {standard: any, value: any}) {
    this.standard = event.standard;
    for (let row of this.rows) {
      row.instance.convertStandard(event);
    }
  }

  offsetTimecodesBy(event: {offset: string, subtract: boolean}) {
    for (let row of this.rows) {
      row.instance.offsetTimecodesBy(event);
    }
  }

  onDisclaimerChanged(event: {field: string, disclaimer: Disclaimer}) {
    let disclaimers: Disclaimer[] = [];
    for (let row of this.rows) {
      let component = row.instance.components.find((c) => c.id === event.field);
      if (component != null && component.disclaimer != null) {
        if (disclaimers.find((d) => d.value == component.disclaimer.value) == null) {
          disclaimers.push(component.disclaimer)
        }
      }
    }
    let field = this.disclaimerFields.find((f) => f.field === event.field)
    if (field != null) {
      let index = this.disclaimerFields.indexOf(field);
      this.disclaimerFields[index].disclaimers = disclaimers;
    } else {
      this.disclaimerFields.push({field: event.field, disclaimers: disclaimers});
    }
    this.disclaimerChanged.emit({field: this.id, value: this.disclaimerFields});
    this.cd.markForCheck();
  }

  updateDisclaimers() {
    let fields = [];
    for (let field of this.disclaimerFields) {
      field.disclaimers = [];
      for (let row of this.rows) {
        let component = row.instance.components.find((c) => c.id === field.field);
        if (component != null && component.disclaimer != null) {
          if (field.disclaimers.find((d) => d.value == component.disclaimer.value) == null) {
            field.disclaimers.push(component.disclaimer)
          }
        }
      }
      if (field.disclaimers.length > 0) {
        fields.push(field)
      }
    }
    this.disclaimerFields = fields;
    this.disclaimerChanged.emit({field: this.id, value: this.disclaimerFields})
    this.cd.markForCheck();
  }

  onShowDisclaimer(event: {field: string, disclaimer: Disclaimer}) {
    let field = this.disclaimerFields.find((f) => f.field === event.field);
    let disclaimer = field.disclaimers.find((d) => d.value === event.disclaimer.value);
    let disclaimerId = event.field + '-' + event.disclaimer.value;
    let element = this.disclaimerElements.toArray().find((e) => e.nativeElement.id === disclaimerId);
    element.nativeElement.scrollIntoViewIfNeeded();
    disclaimer.state = "active";
  }

  updateLinkedFields() {
    for (let row of this.rows) {
      row.instance.updateLinkedFields()
    }
  }

  setCategories(category) { }

  autoIndex() {
    let index = 1;
    for (let row of this.rows) {
      if (this.rows.length > 0 || !row.instance.blank) {
        for (let component of row.instance.components) {
          if (component.field.type === 'auto_index') {
            component.setValue(''+index, false)
            row.value[component.id] = ''+index
          }
        }
        index += 1;
      }
    }
    this.cd.markForCheck();
  }

  setPatternValue(field: string, value: any) { }

  undo(action: UndoAction) {
    let value = action.value;
    this.addRow(value.data, value.position);
    this.cd.markForCheck();
  }

  setLockValues(state) {
    this.lockValues = state;
    for (let row of this.rows) {
      this.setLockValues(state);
    }
  }

  onDragStart() {
    setTimeout(() => {
      this.dragging = true;
      this.cd.markForCheck();
    }, 1);
  }

  onDragEnd() {
    setTimeout(() => {
      this.dragging = false;
      this.cd.markForCheck();
    }, 1);
  }

  onDragDrop(dragRowId: string, dropRowId: string) {
    let dragRow = this.rows.find((r) => r.id == dragRowId);
    let dropRow = this.rows.find((r) => r.id == dropRowId)
    if (dragRow != null && dropRow != null) {
      let container = this.dynamicRows.viewContainerRef;
      let position = container.indexOf(dropRow.ref.hostView);
      container.move(dragRow.ref.hostView, position);
      this.autoIndex();
      this.emitValue();
      this.positionOverlays();
    }
  }
}


// WEBPACK FOOTER //
// ./src/app/forms/table-field.component.ts